import { create } from 'zustand';
import { Service } from '@/types';
import { supabase } from '@/lib/supabase';
import { getCurrentHospitalId } from '@/lib/hospitalContext';
import { hospitalCacheKey } from '@/lib/utils/tenantCache';

interface ServicesState {
  services: Service[];
  isLoading: boolean;
  error: string | null;
  fetchServices: () => Promise<void>;
  addService: (service: Omit<Service, 'id'>) => Promise<void>;
  updateService: (service: Service) => Promise<void>;
  removeService: (id: string) => Promise<void>;
  getServices: () => Service[];
  resetStore: () => void;
}

async function saveServicesCache(services: Service[]) {
  const hospitalId = await getCurrentHospitalId();
  if (!hospitalId) return;
  try {
    localStorage.setItem(
      hospitalCacheKey('services-cache', hospitalId),
      JSON.stringify(services)
    );
  } catch {}
}

export const useServicesStore = create<ServicesState>()((set, get) => ({
  services: [],

  resetStore: () => set({ services: [], isLoading: false, error: null }),
  isLoading: false,
  error: null,

  fetchServices: async () => {
    set({ isLoading: true, error: null });
    try {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital context found');
      }

      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('hospital_id', hospitalId)
        .order('name');

      if (error) throw error;
      set({ services: data || [], isLoading: false });
      void saveServicesCache(data || []);
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  addService: async (service) => {
    set({ isLoading: true, error: null });
    try {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital context found');
      }

      const serviceWithHospital = {
        ...service,
        hospital_id: hospitalId,
      };

      const { data, error } = await supabase
        .from('services')
        .insert([serviceWithHospital])
        .select()
        .single();

      if (error) throw error;
      set((state) => {
        const updated = [...state.services, data];
        void saveServicesCache(updated);
        return { services: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  updateService: async (updatedService) => {
    set({ isLoading: true, error: null });
    try {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital context found');
      }

      const { error } = await supabase
        .from('services')
        .update(updatedService)
        .eq('id', updatedService.id)
        .eq('hospital_id', hospitalId); // Ensure multi-tenant isolation

      if (error) throw error;
      set((state) => {
        const updated = state.services.map((service) =>
          service.id === updatedService.id ? updatedService : service
        );
        void saveServicesCache(updated);
        return { services: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  removeService: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital context found');
      }

      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id)
        .eq('hospital_id', hospitalId); // Ensure multi-tenant isolation

      if (error) throw error;
      set((state) => {
        const updated = state.services.filter((service) => service.id !== id);
        void saveServicesCache(updated);
        return { services: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  getServices: () => get().services,
}));
